/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useState } from "react";
import { Box, Dialog, DialogContent } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { StyledSwitch } from "../total_sales_card/styles";
import { ModalCancelButton } from "../modal_cancel_button";
import { LoadingSpinner } from "../loading_spinner";
import { Loader } from "../loader";
import { StandardPolicyForm } from "./standard_policy_form";
import { PartnerBenefitProgramForm } from "./partner_program_form";
import {
  buildPolicyPayload,
  buildPolicyUpdatePayload,
  collectMissingRequiredPolicyFields,
  INLINE_MESSAGE_FIELDS,
  isStandardPolicyTab,
  PARTNER_PROGRAM_POLICY_TAB,
  POLICY_FIELD_LABELS,
  POLICY_TYPE_OPTIONS,
  validatePolicyConfiguration,
} from "./policy_configuration";
import {
  buildPartnerProgramPayload,
  buildPartnerProgramUpdatePayload,
  collectMissingPartnerProgramFields,
  PARTNER_FIELD_LABELS,
  PARTNER_INLINE_MESSAGE_FIELDS,
  parseEntityKey,
  RECIPIENT_POLICY_LAYER,
  validatePartnerProgram,
} from "./partner_program_configuration";
import {
  CloseIconButton,
  CreateButton,
  DraftBanner,
  DraftHelperText,
  DraftIconWrapper,
  DraftTitle,
  ErrorText,
  FieldContainer,
  FieldLabel,
  FixedActionContainer,
  LoadingPolicyDialogPaperProps,
  ModalLoaderContainer,
  PolicyDialogPaperProps,
  PolicyTypeGroup,
  PolicyTypeOption,
  RequiredMark,
  scrollableContent,
  SectionContainer,
  StyledTextField,
  TitleContainer,
  TitleText,
} from "./styles";

// Re-exported so the page and the tests keep one import path for the form contract,
// while the two forms can share the helpers without importing each other.
export * from "./policy_configuration";
export * from "./partner_program_configuration";
export { StandardPolicyForm } from "./standard_policy_form";
export { PartnerBenefitProgramForm } from "./partner_program_form";

/**
 * The policy modal shell. It owns only what every policy type shares — the draft
 * toggle, the policy name and the type tabs — and swaps the whole body below them,
 * so a new policy family is a new form component rather than another branch per field.
 */
export const PolicyEngineConfigurationModal = ({
  open,
  onClose,
  policyConfigurationData,
  setPolicyConfigurationData,
  onSubmit,
  onValidationError,
  isSubmitting = false,
  mode = "create",
  isLoading = false,
  restaurantGroupDropdown = {},
  restaurantDropdown = {},
  manualUsersDropdown = {},
  activePolicyCheck = {},
  partnerProgramData,
  setPartnerProgramData,
  partnerProgramCheck = {},
  partnerProgramPrefill = {},
  partnershipDropdown = {},
  benefitCardsDropdown = {},
  programExceptionUsersDropdown = {},
  audienceRestaurantsDropdown = {},
  audienceCardsDropdown = {},
  audienceJobsDropdown = {},
  audiencePreview = {},
}) => {
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setErrors({});
    }
  }, [open]);

  const isEditMode = mode === "edit";
  const policyTab = policyConfigurationData?.policy_type;
  const isPartnerProgramTab = policyTab === PARTNER_PROGRAM_POLICY_TAB;
  const isDraft = Boolean(policyConfigurationData?.draft);

  const updateField = (field, value) => {
    setPolicyConfigurationData((currentData) => ({ ...currentData, [field]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [field]: undefined }));
  };

  // Both scope selections are kept when the type is toggled — buildPolicyPayload
  // only ever sends the key matching the chosen type, so the hidden one cannot
  // leak into the request, and a mis-click does not discard the author's work.
  const handlePolicyTypeChange = (policyType) => {
    if (policyType === policyTab) {
      return;
    }

    setPolicyConfigurationData((currentData) => ({ ...currentData, policy_type: policyType }));
    // All of them: the tabs are different forms, and the few keys they share would
    // otherwise outline a control on the tab being entered with no message beside it.
    setErrors({});
  };

  const reportValidationErrors = (validationErrors, missingRequiredFields, fieldLabels, inlineFields) => {
    // A blank member of a required group carries no message of its own, so it gets
    // a generic one — enough to outline its control. The field-level messages are
    // more specific, so they win wherever both apply.
    setErrors({
      ...Object.fromEntries(
        missingRequiredFields.map((field) => [field, `${fieldLabels[field]} is required`])
      ),
      ...validationErrors,
    });
    onValidationError?.({
      missingFields: missingRequiredFields.map((field) => fieldLabels[field]),
      // A rule broken in some way other than being blank is named nowhere else.
      ruleMessages: Object.entries(validationErrors)
        .filter(([field]) => (
          !inlineFields.includes(field) && !missingRequiredFields.includes(field)
        ))
        .map(([, message]) => message),
    });
  };

  const handlePartnerProgramSubmit = () => {
    const validationErrors = validatePartnerProgram(policyConfigurationData, partnerProgramData, isEditMode);
    const missingRequiredFields = collectMissingPartnerProgramFields(
      policyConfigurationData, partnerProgramData, isEditMode
    );

    if (Object.keys(validationErrors).length || missingRequiredFields.length) {
      reportValidationErrors(
        validationErrors, missingRequiredFields, PARTNER_FIELD_LABELS, PARTNER_INLINE_MESSAGE_FIELDS
      );
      return;
    }

    setErrors({});
    onSubmit?.(
      isEditMode
        ? buildPartnerProgramUpdatePayload(policyConfigurationData, partnerProgramData)
        : buildPartnerProgramPayload(policyConfigurationData, partnerProgramData)
    );
  };

  const handleSubmit = () => {
    if (isPartnerProgramTab) {
      handlePartnerProgramSubmit();
      return;
    }

    const validationErrors = validatePolicyConfiguration(policyConfigurationData);
    // The rule controls carry no message of their own — only an outline — so this
    // list is what actually names them, and a control scrolled out of view is
    // invisible until the snackbar does.
    const missingRequiredFields = collectMissingRequiredPolicyFields(policyConfigurationData);

    if (Object.keys(validationErrors).length || missingRequiredFields.length) {
      reportValidationErrors(
        validationErrors, missingRequiredFields, POLICY_FIELD_LABELS, INLINE_MESSAGE_FIELDS
      );
      return;
    }

    setErrors({});
    onSubmit?.(
      isEditMode
        ? buildPolicyUpdatePayload(policyConfigurationData)
        : buildPolicyPayload(policyConfigurationData)
    );
  };

  const isActivePolicyCheckRequired = !isEditMode && !isDraft;
  const hasSelectedScope = policyConfigurationData?.policy_type === "restaurant"
    ? !!policyConfigurationData?.restaurant_ids?.length
    : !!policyConfigurationData?.restaurant_group_id;
  // Saving is blocked from the moment a scope is picked until its check comes back
  // clear. With nothing picked the action stays live on purpose: it cannot produce a
  // request either way, and it is what surfaces the "select a scope" validation.
  const isStandardSubmitBlocked = isActivePolicyCheckRequired && hasSelectedScope && !activePolicyCheck?.isClear;

  const hasProgramSides = Boolean(
    parseEntityKey(partnerProgramData?.provider_key) && parseEntityKey(partnerProgramData?.recipient_key)
  );
  // A recipient policy is authored against the provider's contract, so it cannot be
  // saved before the prefill lands — there would be nothing saying which rules it
  // may even send.
  // Only once a layer is chosen: a pinned pair can complete without one, and blocking
  // there would hide the "Select a policy layer" the submit is what raises.
  // A program leaves draft through this toggle and is retired through the archive
  // action, never the reverse: moving a live provider back to draft would retire the
  // terms its recipients are still enforcing. The endpoint refuses it, so the control
  // is read-only rather than offering a move that always fails.
  const isDraftToggleLocked = isEditMode && isPartnerProgramTab
    && Boolean(partnerProgramData?.status) && partnerProgramData.status !== "draft";

  // An edit runs no pair check — the active policy occupying the pair is the one being
  // edited — so only the recipient's prefill can still block it.
  const isPartnerSubmitBlocked = hasProgramSides && Boolean(partnerProgramData?.policy_layer) && (
    (!isDraft && !isEditMode && !partnerProgramCheck?.isClear)
    || (partnerProgramData?.policy_layer === RECIPIENT_POLICY_LAYER && !partnerProgramData?.prefill)
  );
  const isSubmitBlocked = isPartnerProgramTab ? isPartnerSubmitBlocked : isStandardSubmitBlocked;

  // Creating names what the save produces (a draft or a live policy); updating
  // names the action itself, since the draft toggle is only one of its effects.
  const submitLabel = isEditMode ? "Update Policy" : (isDraft ? "Draft Policy" : "Create Policy");

  return (
    <Dialog
      data-testid="policy-engine-configuration-modal"
      open={open}
      onClose={onClose}
      // The program form runs two columns and three-control rule rows, which do not
      // fit the scope form's width — the body changes wholesale with the tab anyway.
      maxWidth={isPartnerProgramTab ? "md" : "sm"}
      fullWidth
      PaperProps={isLoading ? LoadingPolicyDialogPaperProps : PolicyDialogPaperProps}
    >
      <TitleContainer>
        <TitleText data-testid="policy-engine-modal-title">
          {isEditMode ? "Edit Policy" : "Create Policy"}
        </TitleText>
        <CloseIconButton
          data-testid="policy-engine-modal-close-btn"
          onClick={onClose}
          disableRipple
        >
          <ClearIcon fontSize="small" />
        </CloseIconButton>
      </TitleContainer>

      {/* Edit only: the form is populated from the fetched policy, so the
          loader stands in for it rather than fields filling in afterwards. */}
      {isLoading ? (
        <ModalLoaderContainer data-testid="policy-engine-modal-loader">
          <Loader />
        </ModalLoaderContainer>
      ) : (
        <DialogContent sx={scrollableContent}>
          {/* Draft, name and type — shared by every policy family. */}
          <SectionContainer sx={{ paddingBottom: "6px" }}>
            <DraftBanner isDraft={isDraft}>
              <Box sx={{ display: "flex", gap: "10px" }}>
                <DraftIconWrapper isDraft={isDraft}>
                  {isDraft ? <DescriptionOutlinedIcon /> : <TaskAltIcon />}
                </DraftIconWrapper>
                <Box>
                  <DraftTitle>Draft</DraftTitle>
                  <DraftHelperText>
                    {isDraftToggleLocked
                      ? "This program is already live. Archive it to take it out of force."
                      : (isDraft
                        ? "Saved but not enforced. Turn off to make this policy live."
                        : "Off — this policy takes effect as soon as it's saved.")}
                  </DraftHelperText>
                </Box>
              </Box>
              <StyledSwitch
                data-testid="policy-engine-draft-switch"
                checked={isDraft}
                disabled={isDraftToggleLocked}
                onChange={(event) => updateField("draft", event.target.checked)}
              />
            </DraftBanner>

            <FieldContainer sx={{ marginTop: "16px" }}>
              <FieldLabel>Policy name<RequiredMark>*</RequiredMark></FieldLabel>
              <StyledTextField
                inputProps={{ "data-testid": "policy-engine-name-input" }}
                fullWidth
                size="small"
                placeholder="Executive Policy"
                value={policyConfigurationData?.name || ""}
                onChange={(event) => updateField("name", event.target.value)}
                error={!!errors?.name}
                autoComplete="off"
              />
              {!!errors?.name && <ErrorText>{errors.name}</ErrorText>}
            </FieldContainer>

            {/* Locked while editing: the update endpoint takes neither the policy
                type nor a scope, so a policy keeps the scope it was created with. */}
            <FieldContainer sx={{ marginBottom: 0 }}>
              <FieldLabel>Policy type<RequiredMark>*</RequiredMark></FieldLabel>
              <PolicyTypeGroup>
                {POLICY_TYPE_OPTIONS.map((option) => (
                  <PolicyTypeOption
                    key={option.value}
                    data-testid={`policy-engine-policy-type-${option.value}`}
                    disableRipple
                    disabled={isEditMode}
                    isSelected={policyTab === option.value}
                    onClick={() => handlePolicyTypeChange(option.value)}
                  >
                    {option.label}
                  </PolicyTypeOption>
                ))}
              </PolicyTypeGroup>
            </FieldContainer>
          </SectionContainer>

          {isStandardPolicyTab(policyTab) && (
            <StandardPolicyForm
              policyConfigurationData={policyConfigurationData}
              setPolicyConfigurationData={setPolicyConfigurationData}
              errors={errors}
              setErrors={setErrors}
              isEditMode={isEditMode}
              activePolicyCheck={activePolicyCheck}
              restaurantGroupDropdown={restaurantGroupDropdown}
              restaurantDropdown={restaurantDropdown}
              manualUsersDropdown={manualUsersDropdown}
            />
          )}

          {isPartnerProgramTab && (
            <PartnerBenefitProgramForm
              policyConfigurationData={policyConfigurationData}
              partnerProgramData={partnerProgramData}
              setPartnerProgramData={setPartnerProgramData}
              errors={errors}
              setErrors={setErrors}
              isEditMode={isEditMode}
              activePolicyCheck={partnerProgramCheck}
              prefillStatus={partnerProgramPrefill}
              partnershipDropdown={partnershipDropdown}
              benefitCardsDropdown={benefitCardsDropdown}
              exceptionUsersDropdown={programExceptionUsersDropdown}
              audienceRestaurantsDropdown={audienceRestaurantsDropdown}
              audienceCardsDropdown={audienceCardsDropdown}
              audienceJobsDropdown={audienceJobsDropdown}
              audiencePreview={audiencePreview}
            />
          )}
        </DialogContent>
      )}

      <FixedActionContainer>
        <ModalCancelButton
          data-testid="policy-engine-modal-cancel-btn"
          disableRipple
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </ModalCancelButton>
        <CreateButton
          data-testid="policy-engine-modal-create-btn"
          disableRipple
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || isLoading || isSubmitBlocked}
        >
          {isSubmitting ? <LoadingSpinner isLoading={true} /> : submitLabel}
        </CreateButton>
      </FixedActionContainer>
    </Dialog>
  );
};
